import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaChartBar, FaCog, FaFileAlt, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const SidebarNavigation = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAISettings, setShowAISettings] = useState(false);
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [aiSettingsStatus, setAISettingsStatus] = useState(null);
  const [aiSettingsError, setAISettingsError] = useState('');
  const [loadingAISettings, setLoadingAISettings] = useState(false);
  const [savingAISettings, setSavingAISettings] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const openAISettings = async () => {
    setShowAISettings(true);
    setAISettingsError('');
    setAISettingsStatus(null);
    setLoadingAISettings(true);
    try {
      const settings = await apiService.getAISettings();
      setAISettingsStatus(settings);
      setGeminiApiKey('');
    } catch (error) {
      setAISettingsError('Failed to load AI settings.');
    } finally {
      setLoadingAISettings(false);
    }
  };

  const handleSaveAISettings = async () => {
    setSavingAISettings(true);
    setAISettingsError('');
    try {
      const result = await apiService.saveAISettings({ gemini_api_key: geminiApiKey });
      setAISettingsStatus(result);
      setGeminiApiKey('');
    } catch (error) {
      setAISettingsError('Failed to save Gemini API key.');
    } finally {
      setSavingAISettings(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <button
        className="sidebar-mobile-toggle"
        type="button"
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
      >
        {mobileOpen ? <FaTimes /> : <FaBars />}
      </button>

      {mobileOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close sidebar"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`sidebar-nav ${mobileOpen ? 'is-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            <div className="sidebar-brand-icon">AI</div>
            <h1>No Man SOC</h1>
          </div>
          <p> v1.0</p>
        </div>

        <Link className="sidebar-cta" to="/generate-report">
          <FaFileAlt />
          <span>Generate New Report</span>
        </Link>

        <nav className="sidebar-main-nav">
          <NavLink to="/dashboard" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FaChartBar />
            <span>Report Dashboard</span>
          </NavLink>
          <NavLink to="/report" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FaFileAlt />
            <span>Archive Gallery</span>
          </NavLink>
          <NavLink to="/edr-management" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FaCog />
            <span>API Configuration</span>
          </NavLink>
        </nav>

        <div className="sidebar-footer-nav">
          <button type="button" className="sidebar-link sidebar-button-link" onClick={openAISettings}>
            <FaCog />
            <span>AI Settings</span>
          </button>
          <button type="button" className="sidebar-link sidebar-button-link" onClick={logout}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
          <div className="sidebar-user">
            <p className="sidebar-user-name">{user?.username || 'Admin'}</p>
            <p className="sidebar-user-role">Role: {user?.role || 'admin'}</p>
          </div>
        </div>
      </aside>

      <Modal show={showAISettings} onHide={() => setShowAISettings(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>AI Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {aiSettingsError && <Alert variant="danger">{aiSettingsError}</Alert>}
          {aiSettingsStatus && (
            <Alert variant={aiSettingsStatus.gemini_configured ? 'success' : 'warning'}>
              Gemini status: {aiSettingsStatus.gemini_configured ? `Configured (${aiSettingsStatus.gemini_key_hint})` : 'Not configured'}
            </Alert>
          )}
          <Form.Group>
            <Form.Label>Gemini API Key</Form.Label>
            <Form.Control
              type="password"
              placeholder="Paste Gemini API key"
              value={geminiApiKey}
              onChange={(event) => setGeminiApiKey(event.target.value)}
              disabled={loadingAISettings || savingAISettings}
            />
            <Form.Text className="text-muted">
              Admin must save this first before report generation can use the Gemini engine.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAISettings(false)} disabled={savingAISettings}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveAISettings} disabled={loadingAISettings || savingAISettings}>
            {savingAISettings ? 'Saving...' : 'Save Gemini Key'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SidebarNavigation;
